import React, {useState, forwardRef, useImperativeHandle, useRef, useEffect} from 'react'
import {Modal, ModalHeader, ModalActions, Button, Alert} from '../../UI/index.js'
import {formSpacing, FormRow, Input} from '../../UI/forms/index.js'
import type {ParcelProducts, ParcelPriceTable} from '../../../../types/types.js'

// Type for Input component ref (class component)
interface InputRef {
    focus: () => void
    select: () => void
    blur: () => void
    getRef: () => HTMLInputElement
    setInvalid: (isInvalid?: boolean) => void
}

type PriceScale = ParcelPriceTable[string][string]['countryGroups'][0]['priceScales'][0]

interface ScaleInfo {
    carrierName: string
    product: ParcelProducts
    countryGroupIndex: number
    categoryIndex?: number
    scaleIndex?: number
}

interface PriceScaleModalProps {
    onRemove: (
        carrierName: string,
        product: ParcelProducts,
        countryGroupIndex: number,
        categoryIndex: number,
        scaleIndex: number
    ) => void
    onSubmit: (
        carrierName: string,
        product: ParcelProducts,
        countryGroupIndex: number,
        categoryIndex: number,
        scaleIndex: number,
        scale: PriceScale
    ) => void
}

export interface PriceScaleModalRef {
    open: (
        carrierName: string,
        product: ParcelProducts,
        countryGroupIndex: number,
        categoryIndex?: number,
        scaleIndex?: number,
        scale?: PriceScale
    ) => void
    close: () => void
    setWarnings: React.Dispatch<React.SetStateAction<string[]>>
}

const PriceScaleModal = forwardRef<PriceScaleModalRef, PriceScaleModalProps>(({onRemove, onSubmit}, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const [scale, setScale] = useState<PriceScale | undefined>(undefined)
    const [scaleInfo, setScaleInfo] = useState<ScaleInfo | undefined>(undefined)
    const [warnings, setWarnings] = useState<string[]>([])
    const inputRef = useRef<InputRef | null>(null)

    const openModal = (
        carrierName: string,
        product: ParcelProducts,
        countryGroupIndex: number,
        categoryIndex?: number,
        scaleIndex?: number,
        scale: PriceScale = {maxAmount: 0, price: ''}
    ) => {
        setIsOpen(true)
        setScale(scale)
        setScaleInfo({
            carrierName,
            product,
            countryGroupIndex,
            categoryIndex,
            scaleIndex
        })
    }

    const closeModal = () => {
        setIsOpen(false)
        setScale(undefined)
        setScaleInfo(undefined)
    }

    useImperativeHandle(ref, () => ({
        open: openModal,
        close: closeModal,
        setWarnings
    }))

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus()
        }
    }, [isOpen])

    return (
        <Modal style={{width: 400}} show={isOpen} onClose={() => setIsOpen(false)}>
            <ModalHeader title='Staffel' onClose={() => setIsOpen(false)} style={{marginBottom: 0}} />

            <FormRow style={{marginBottom: formSpacing.rowGap}}>
                <Input
                    label='Maximaal aantal stuks'
                    value={scale?.maxAmount}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setScale({
                            ...scale,
                            maxAmount: parseInt(event.target.value) || 0,
                            price: scale?.price || ''
                        })
                    }}
                    ref={(ref: InputRef | null) => inputRef.current = ref}
                />
            </FormRow>

            {warnings && warnings.length > 0 && warnings.map((warning, index) => (
                <Alert style={{marginBottom: 6}} key={index} variant='warning'>{warning}</Alert>
            ))}

            <br />

            <ModalActions>
                {typeof scaleInfo?.scaleIndex === 'number' && typeof scaleInfo?.categoryIndex === 'number' && (
                    <Button
                        variant='text'
                        onClick={() => {
                            const {carrierName, product, countryGroupIndex, categoryIndex, scaleIndex} = scaleInfo
                            onRemove(carrierName, product, countryGroupIndex, categoryIndex!, scaleIndex!)
                        }}
                    >
                        Verwijder
                    </Button>
                )}
                <Button
                    variant='text'
                    onClick={() => {
                        if (scaleInfo && scale) {
                            const {carrierName, product, countryGroupIndex, categoryIndex, scaleIndex} = scaleInfo
                            onSubmit(carrierName, product, countryGroupIndex, categoryIndex!, scaleIndex!, scale)
                        }
                    }}
                >
                    Opslaan
                </Button>
            </ModalActions>
        </Modal>
    )
})

PriceScaleModal.displayName = 'PriceScaleModal'

export default PriceScaleModal
